import { useState, type FormEvent } from 'react';

import { Button, Card, Field, Input } from '@/design-system';
import {
    LOW_CREDIT_THRESHOLD_MAX,
    type TenantSettings,
    type TenantSettingsPatch,
} from '@/entities/tenant';
import { isApiError } from '@/shared/api';

import { creditsCopy } from '../copy';

export interface CreditsCardProps {
    settings: TenantSettings;
    /** De waarschuwing gaat met een knop. */
    onSave: (patch: TenantSettingsPatch, done: () => void) => void;
    saving?: boolean;
    disabled?: boolean;
    error?: unknown;
}

/** Een heel aantal keuringen tussen 0 en `max`, of `null` als het dat niet is. */
function keuringen(value: string, max: number): number | null {
    if (!/^\d+$/.test(value.trim())) {
        return null;
    }

    const parsed = Number(value.trim());

    return parsed <= max ? parsed : null;
}

/** Wat er gebeurt als de keuringen op zijn, in de woorden van de eigenaar. */
function ruleText(settings: TenantSettings): string {
    if (!settings.blockOnEmptyCredits) {
        return creditsCopy.ruleNoBlock;
    }

    return settings.graceCredits > 0
        ? `${creditsCopy.ruleBlock} ${creditsCopy.ruleGrace(settings.graceCredits)}`
        : creditsCopy.ruleBlock;
}

/**
 * Tegoed: wat er gebeurt als de keuringen op zijn, en vanaf wanneer de app
 * waarschuwt.
 *
 * Of nieuwe keuringen stoppen en hoe ver het saldo onder nul mag
 * (`blockOnEmptyCredits`, `graceCredits`) regelt Smartkeur: daarmee maakt een
 * bedrijf anders keuringen die het niet betaalde. De eigenaar leest het hier als
 * uitleg, zonder schakelaar, en de API weigert een wijziging. Wat de eigenaar zelf
 * zet, is de waarschuwing (`lowCreditThreshold`); de API zet die meteen op de
 * wallet.
 *
 * Alleen voor wie instellingen beheert; de stand van het tegoed zelf staat bij
 * Facturatie.
 */
export function CreditsCard({
    settings,
    onSave,
    saving = false,
    disabled = false,
    error = null,
}: CreditsCardProps) {
    const [threshold, setThreshold] = useState(String(settings.lowCreditThreshold));
    const [touched, setTouched] = useState(false);
    const [saved, setSaved] = useState(false);

    const thresholdValue = keuringen(threshold, LOW_CREDIT_THRESHOLD_MAX);
    const apiField = (field: string) =>
        isApiError(error) ? error.firstValidationError(field) : undefined;

    function submit(event: FormEvent) {
        event.preventDefault();
        setTouched(true);
        setSaved(false);

        if (thresholdValue === null) {
            return;
        }

        onSave({ lowCreditThreshold: thresholdValue }, () => setSaved(true));
    }

    return (
        <Card padding="lg" as="section" aria-labelledby="credits-title">
            <h2
                id="credits-title"
                className="font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {creditsCopy.title}
            </h2>
            <p className="mt-1 mb-[6px] text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {creditsCopy.intro}
            </p>

            <div className="border-b border-[var(--border-subtle-2)] pt-1 pb-[14px]">
                <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-primary)]">
                    {ruleText(settings)}
                </p>
                <p className="m-0 mt-1 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {creditsCopy.ruleSetBy}
                </p>
            </div>

            <form onSubmit={submit} noValidate className="grid gap-[10px] pt-[14px] sm:grid-cols-2">
                <Field
                    label={creditsCopy.threshold}
                    description={creditsCopy.thresholdHint(LOW_CREDIT_THRESHOLD_MAX)}
                    error={
                        touched && thresholdValue === null
                            ? creditsCopy.invalid(LOW_CREDIT_THRESHOLD_MAX)
                            : apiField('lowCreditThreshold')
                    }
                >
                    <Input
                        mono
                        inputMode="numeric"
                        value={threshold}
                        onChange={(event) => {
                            setSaved(false);
                            setThreshold(event.target.value);
                        }}
                    />
                </Field>

                <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
                    <Button type="submit" variant="outline" size="sm" disabled={saving || disabled}>
                        {saving ? creditsCopy.saving : creditsCopy.save}
                    </Button>
                    {saved ? (
                        <span
                            role="status"
                            className="text-[12px] leading-[1.4] text-[var(--accent-dark)]"
                        >
                            {creditsCopy.saved}
                        </span>
                    ) : null}
                </div>
            </form>
        </Card>
    );
}
